import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../api';
import type { AuditFinding, DomainDetail, HistoryAuditEntry } from '../../types';
import { CitationHistorySection } from '../CitationHistorySection';
import { SchemaDeliveryCard } from '../dashboard/SchemaDeliveryCard';
import { DeliveryRouteChooser } from '../onboarding/steps/DeliveryRouteChooser';
import { getScoreLabel, getLabelTone, type ScoreLabel } from '@fixmyseo/shared/label-bands';

const POLL_INTERVAL_MS = 15_000;
const POLL_MAX_MS = 5 * 60 * 1000;
const NEW_DOMAIN_DAY_THRESHOLD = 30;
const NEW_DOMAIN_AUDIT_THRESHOLD = 3;

interface Props {
  domain: DomainDetail;
  onVerified: () => void;
  onAiTruthSetup?: () => void;
}

const TONE_STATUS_CLS: Record<ReturnType<typeof getLabelTone>, string> = {
  green:   'bg-green-100 text-green-800',
  emerald: 'bg-emerald-100 text-emerald-700',
  amber:   'bg-amber-100 text-amber-700',
  orange:  'bg-orange-100 text-orange-700',
  red:     'bg-red-100 text-red-700',
};

const TONE_PILLAR_BG: Record<ReturnType<typeof getLabelTone>, string> = {
  green:   '#f0fdf4',
  emerald: '#ecfdf5',
  amber:   '#fffbeb',
  orange:  '#fff7ed',
  red:     '#fef2f2',
};

// ── Score history line chart ────────────────────────────────────────────────

function ScoreHistoryChart({ audits }: { audits: HistoryAuditEntry[] }) {
  // audits arrive oldest-first from the API — plot as-is left-to-right
  const entries = [...audits];
  if (entries.length < 2) return null;

  const W = 600, H = 120;
  const PAD = { t: 8, r: 12, b: 22, l: 28 };
  const iW = W - PAD.l - PAD.r;
  const iH = H - PAD.t - PAD.b;
  const n  = entries.length;

  const cx = (i: number) => PAD.l + (i / Math.max(n - 1, 1)) * iW;
  const cy = (v: number) => PAD.t + iH - (Math.min(100, Math.max(0, v)) / 100) * iH;
  const pts = (vals: number[]) => vals.map((v, i) => `${cx(i)},${cy(v)}`).join(' ');

  const seo = entries.map(e => (e.currentScores ?? e.originalScores).seo);
  const aeo = entries.map(e => (e.currentScores ?? e.originalScores).aeo);
  const geo = entries.map(e => (e.currentScores ?? e.originalScores).geo);

  const xLabels = [0, Math.floor((n - 1) / 2), n - 1].filter((v, i, a) => a.indexOf(v) === i);

  return (
    <div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ height: 120 }} aria-hidden="true">
        {[0, 50, 100].map(t => (
          <g key={t}>
            <line x1={PAD.l} x2={W - PAD.r} y1={cy(t)} y2={cy(t)} stroke="#e2e8f0" strokeWidth="1" />
            <text x={PAD.l - 4} y={cy(t) + 3.5} textAnchor="end" fontSize="8" fill="#94a3b8">{t}</text>
          </g>
        ))}
        <polyline points={pts(seo)} fill="none" stroke="#6366f1" strokeWidth="1.8" strokeLinejoin="round" />
        <polyline points={pts(aeo)} fill="none" stroke="#0891b2" strokeWidth="1.8" strokeLinejoin="round" />
        <polyline points={pts(geo)} fill="none" stroke="#059669" strokeWidth="1.8" strokeLinejoin="round" />
        {xLabels.map(i => (
          <text key={i} x={cx(i)} y={H - 4} textAnchor="middle" fontSize="8" fill="#94a3b8">
            {new Date(entries[i]!.auditedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
          </text>
        ))}
      </svg>
      <div className="flex items-center gap-5 mt-2">
        {[
          { label: 'SEO', color: '#6366f1' },
          { label: 'AEO', color: '#0891b2' },
          { label: 'GEO', color: '#059669' },
        ].map(({ label, color }) => (
          <span key={label} className="flex items-center gap-1.5 text-xs text-gray-500">
            <span className="w-4 h-0.5 rounded-full inline-block" style={{ background: color }} />
            {label}
          </span>
        ))}
      </div>
    </div>
  );
}

// ── Check catalog — canonical check IDs aligned with STANDARD_IMPACT ──────

type CheckMeta = {
  label: string;
  pillar: 'seo' | 'aeo' | 'geo';
  impactBand: 'high' | 'medium' | 'low';
  description: string;
  dataNeeded: string;
  pendingReason: string;
};

const CHECK_CATALOG: Record<string, CheckMeta> = {
  // ── SEO ─────────────────────────────────────────────────────────────────
  'seo.https': {
    label: 'HTTPS enabled', pillar: 'seo', impactBand: 'high',
    description: 'Verifies the site loads over HTTPS — a baseline trust signal for all search engines and AI crawlers.',
    dataNeeded: 'Site reachability check during crawl.',
    pendingReason: 'The site was unreachable or the crawl did not complete.',
  },
  'seo.mobile_viewport': {
    label: 'Mobile viewport', pillar: 'seo', impactBand: 'high',
    description: 'Tests for a correct <meta name="viewport"> tag so the page renders properly on mobile.',
    dataNeeded: 'HTML crawl of the home page.',
    pendingReason: 'The page HTML was not crawled in this scan.',
  },
  'seo.ai_bot_access': {
    label: 'AI crawler access', pillar: 'seo', impactBand: 'high',
    description: 'Checks that AI crawlers (GPTBot, ClaudeBot, Google-Extended, PerplexityBot) are not blocked in robots.txt.',
    dataNeeded: 'robots.txt fetch from your domain root.',
    pendingReason: 'robots.txt was inaccessible or returned an error during the scan.',
  },
  'seo.title_tag': {
    label: 'Title tag', pillar: 'seo', impactBand: 'high',
    description: 'Checks the <title> tag is present and between 30–60 characters — the primary label search engines and AI use for the page.',
    dataNeeded: 'HTML crawl.',
    pendingReason: 'Page HTML was not crawled in this scan.',
  },
  'seo.meta_description': {
    label: 'Meta description', pillar: 'seo', impactBand: 'medium',
    description: 'Verifies a meta description is present and 50–160 characters — used as the page snippet in search results.',
    dataNeeded: 'HTML crawl.',
    pendingReason: 'Page HTML was not crawled in this scan.',
  },
  'seo.single_h1': {
    label: 'Single H1 heading', pillar: 'seo', impactBand: 'medium',
    description: 'Verifies exactly one H1 element is present — multiple or missing H1s confuse crawlers about the page\'s primary topic.',
    dataNeeded: 'HTML crawl.',
    pendingReason: 'Page HTML was not crawled in this scan.',
  },
  'seo.cwv_lcp': {
    label: 'Largest Contentful Paint', pillar: 'seo', impactBand: 'medium',
    description: 'Measures perceived load speed — how long until the largest visible element appears. Pass: ≤2.5s, Warning: ≤4s.',
    dataNeeded: 'PageSpeed Insights API or Chrome User Experience Report (CrUX) field data.',
    pendingReason: 'Core Web Vitals data was not collected in this scan — usually requires sufficient real-user traffic.',
  },
  'seo.canonical': {
    label: 'Canonical tag', pillar: 'seo', impactBand: 'low',
    description: 'Checks a <link rel="canonical"> is present and self-referencing — prevents duplicate-content penalties.',
    dataNeeded: 'HTML crawl.',
    pendingReason: 'Page HTML was not crawled in this scan.',
  },
  'seo.og_tags': {
    label: 'Open Graph tags', pillar: 'seo', impactBand: 'low',
    description: 'Verifies og:title, og:description, and og:image are present — required for rich previews in social and AI answer cards.',
    dataNeeded: 'HTML crawl.',
    pendingReason: 'Page HTML was not crawled in this scan.',
  },
  'seo.image_alt_coverage': {
    label: 'Image alt text coverage', pillar: 'seo', impactBand: 'low',
    description: 'Measures the percentage of <img> elements that have alt attributes — improves accessibility and image indexability.',
    dataNeeded: 'HTML crawl.',
    pendingReason: 'Page HTML was not crawled in this scan.',
  },
  'seo.review_signals': {
    label: 'Review signals', pillar: 'seo', impactBand: 'medium',
    description: 'Detects review structured data (AggregateRating) or visible star ratings — a trust signal used in rich snippets and AI summaries.',
    dataNeeded: 'HTML and structured data extraction.',
    pendingReason: 'Structured data was not extracted in this scan.',
  },
  // ── AEO ─────────────────────────────────────────────────────────────────
  'aeo.faq_schema': {
    label: 'FAQ schema markup', pillar: 'aeo', impactBand: 'high',
    description: 'Checks for FAQPage or Q&A schema — the primary signal that unlocks AI-generated answer cards and featured snippets.',
    dataNeeded: 'Structured data extraction from page HTML.',
    pendingReason: 'Structured data was not extracted in this scan.',
  },
  'aeo.heading_hierarchy': {
    label: 'Question-based headings', pillar: 'aeo', impactBand: 'medium',
    description: 'Verifies H2/H3 headings are phrased as questions — signals Q&A intent that AI platforms use to extract direct answers.',
    dataNeeded: 'HTML crawl.',
    pendingReason: 'Page HTML was not crawled in this scan.',
  },
  'aeo.direct_answer_opening': {
    label: 'Direct-answer opening', pillar: 'aeo', impactBand: 'low',
    description: 'Checks whether the first paragraph directly answers the page\'s primary question — AI answer engines prefer pages that lead with the answer.',
    dataNeeded: 'HTML crawl and content analysis.',
    pendingReason: 'Content analysis was not run in this scan.',
  },
  'aeo.article_schema': {
    label: 'Article schema', pillar: 'aeo', impactBand: 'medium',
    description: 'Verifies Article or BlogPosting structured data is present — helps AI identify content type and extract publication metadata.',
    dataNeeded: 'Structured data extraction.',
    pendingReason: 'Structured data was not extracted in this scan.',
  },
  'aeo.organization_schema': {
    label: 'Organization schema', pillar: 'aeo', impactBand: 'medium',
    description: 'Checks for Organization schema with name, url, and logo — foundational for entity recognition by AI knowledge graphs.',
    dataNeeded: 'Structured data extraction.',
    pendingReason: 'Structured data was not extracted in this scan.',
  },
  // ── GEO ─────────────────────────────────────────────────────────────────
  'geo.llms_txt_present': {
    label: 'llms.txt file', pillar: 'geo', impactBand: 'medium',
    description: 'Checks for a /llms.txt file — a signal to LLM crawlers about how your content may be used, analogous to robots.txt for AI.',
    dataNeeded: 'llms.txt fetch from your domain root.',
    pendingReason: 'This check only runs on full scheduled audit cycles, not on-demand scans.',
  },
  'geo.sameas_authority': {
    label: 'sameAs authority links', pillar: 'geo', impactBand: 'medium',
    description: 'Verifies sameAs links in Organization schema point to authoritative entity sources (Wikipedia, Wikidata, Companies House, etc.) that AI knowledge graphs trust.',
    dataNeeded: 'Organization schema extraction from structured data.',
    pendingReason: 'No Organization schema was found, or structured data was not extracted in this scan.',
  },
  'geo.directory_presence': {
    label: 'Directory listings', pillar: 'geo', impactBand: 'medium',
    description: 'Measures the percentage of major business directories (Google Business, Yelp, Apple Maps, Bing Places, etc.) where your business is listed — a citation signal AI platforms weight heavily.',
    dataNeeded: 'Citation data from directory APIs (collected on scheduled audit cycles).',
    pendingReason: 'Directory citation data is collected on full scheduled audit cycles, not on-demand scans.',
  },
  'geo.about_page_depth': {
    label: 'About page depth', pillar: 'geo', impactBand: 'medium',
    description: 'Evaluates whether your About page exists and has sufficient depth — entity mentions, word count, and internal links that help AI models form a clear picture of your business.',
    dataNeeded: 'Separate crawl of your /about page (or equivalent).',
    pendingReason: 'The About page was not separately crawled in this scan.',
  },
  'geo.entity_consistency': {
    label: 'Entity name consistency', pillar: 'geo', impactBand: 'medium',
    description: 'Checks that your business name is identical across structured data, page title, and directory citations — inconsistency causes AI models to treat them as different entities.',
    dataNeeded: 'Cross-signal comparison: structured data + crawl + citation data.',
    pendingReason: 'Requires citation data alongside the crawl — not all data sources were collected in this scan.',
  },
  'geo.review_platform_presence': {
    label: 'Review platform presence', pillar: 'geo', impactBand: 'medium',
    description: 'Checks if your business has a verified presence and reviews on major platforms (Google, Yelp, Trustpilot) — AI platforms use review signals to gauge entity credibility.',
    dataNeeded: 'Review platform check (collected on scheduled audit cycles).',
    pendingReason: 'Review platform data is collected on full scheduled audit cycles, not on-demand scans.',
  },
  'geo.inbound_citation_velocity': {
    label: 'Citation velocity trend', pillar: 'geo', impactBand: 'high',
    description: 'Tracks whether AI citation mentions of your business are growing, stable, or declining over time — a compounding signal: the more AI cites you, the more it cites you.',
    dataNeeded: 'Two or more completed audit cycles.',
    pendingReason: 'Requires at least 2 audit cycles to calculate a trend. Run another audit after your next scheduled scan.',
  },
};

const CANONICAL_CHECK_IDS = new Set(Object.keys(CHECK_CATALOG));

const VERDICT_CONFIG = {
  pass:        { label: 'Pass',    cls: 'bg-green-100 text-green-800' },
  warning:     { label: 'Warning', cls: 'bg-amber-100 text-amber-700' },
  fail:        { label: 'Fail',    cls: 'bg-red-100 text-red-700' },
  not_checked: { label: 'Pending', cls: 'bg-gray-100 text-gray-400' },
} as const;

const BAND_ORDER = { high: 0, medium: 1, low: 2 } as const;

// ── Check tooltip ───────────────────────────────────────────────────────────

function CheckTooltip({ meta, isPending }: { meta: CheckMeta; isPending: boolean }) {
  const btnRef = useRef<HTMLButtonElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  function show() {
    if (btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      setPos({ top: r.top, left: r.right + 6 });
    }
  }

  return (
    <span className="inline-flex shrink-0">
      <button
        ref={btnRef}
        className="w-3.5 h-3.5 rounded-full bg-gray-100 text-gray-400 text-[8px] font-bold flex items-center justify-center hover:bg-gray-200 focus:outline-none focus-visible:ring-1 focus-visible:ring-gray-400"
        onMouseEnter={show}
        onMouseLeave={() => setPos(null)}
        onFocus={show}
        onBlur={() => setPos(null)}
        tabIndex={-1}
        aria-label="Check explanation"
        type="button"
      >
        ?
      </button>
      {pos && createPortal(
        <span
          className="fixed z-[9999] w-60 bg-gray-900 text-white text-[10px] rounded-lg p-2.5 shadow-2xl leading-relaxed pointer-events-none"
          style={{ top: pos.top, left: pos.left }}
        >
          <span className="block mb-1">{meta.description}</span>
          <span className="block text-gray-300">
            <span className="font-semibold text-gray-100">Needs: </span>{meta.dataNeeded}
          </span>
          {isPending && (
            <span className="block text-amber-300 mt-1.5">
              <span className="font-semibold">Why pending: </span>{meta.pendingReason}
            </span>
          )}
        </span>,
        document.body,
      )}
    </span>
  );
}

// ── Findings modal ─────────────────────────────────────────────────────────

function FindingsModal({
  pillar,
  auditId,
  onClose,
}: {
  pillar: 'seo' | 'aeo' | 'geo';
  auditId: string;
  onClose: () => void;
}) {
  const [rows, setRows] = useState<AuditFinding[] | null>(null);
  const [auditedAt, setAuditedAt] = useState<string | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.audits.getFindings(auditId)
      .then((res) => {
        // Build a map from API results, filtering out any non-canonical check IDs
        // (e.g. geo.perplexity_branded that bleed in from scoring inputs).
        const apiMap = new Map<string, AuditFinding>(
          res.findings
            .filter((f) => f.pillar === pillar && CANONICAL_CHECK_IDS.has(f.checkId))
            .map((f) => [f.checkId, f]),
        );

        // Merge: every canonical check for this pillar gets a row.
        // Checks absent from the API response show as not_checked.
        const merged: AuditFinding[] = Object.entries(CHECK_CATALOG)
          .filter(([, meta]) => meta.pillar === pillar)
          .map(([id, meta]) => {
            const api = apiMap.get(id);
            if (api) return api;
            return {
              checkId: id,
              pillar,
              verdict: 'not_checked' as const,
              impactBand: meta.impactBand,
              evidence: {} as Record<string, unknown>,
              previousVerdict: null,
            };
          });

        setRows(merged);
        setAuditedAt(res.auditedAt ?? null);
      })
      .catch(() => setError(true));
  }, [auditId, pillar]);

  const sorted = rows
    ? [...rows].sort((a, b) => {
        if (a.verdict === 'not_checked' && b.verdict !== 'not_checked') return 1;
        if (a.verdict !== 'not_checked' && b.verdict === 'not_checked') return -1;
        const bandDiff = BAND_ORDER[a.impactBand as keyof typeof BAND_ORDER] - BAND_ORDER[b.impactBand as keyof typeof BAND_ORDER];
        if (bandDiff !== 0) return bandDiff;
        const vOrder = { fail: 0, warning: 1, pass: 2, not_checked: 3 } as const;
        return (vOrder[a.verdict as keyof typeof vOrder] ?? 3) - (vOrder[b.verdict as keyof typeof vOrder] ?? 3);
      })
    : null;

  const pendingCount = sorted ? sorted.filter((f) => f.verdict === 'not_checked').length : 0;
  const PILLAR_LABELS = { seo: 'SEO', aeo: 'AEO', geo: 'GEO' };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div>
            <h2 className="font-semibold text-gray-900 text-sm">
              {PILLAR_LABELS[pillar]} checks
            </h2>
            {auditedAt && (
              <p className="text-[10px] text-gray-400 mt-0.5">
                Scanned {new Date(auditedAt).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' })}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-lg leading-none"
          >
            ×
          </button>
        </div>

        <div className="overflow-y-auto flex-1 px-5 py-3">
          {error && (
            <p className="text-sm text-red-500 py-4 text-center">Could not load check results.</p>
          )}
          {!error && !sorted && (
            <div className="flex justify-center py-8">
              <div className="w-5 h-5 rounded-full border-2 border-brand-green border-t-transparent animate-spin" />
            </div>
          )}
          {sorted && pendingCount > 0 && (
            <p className="text-[10px] text-gray-400 pb-2">
              {pendingCount} check{pendingCount > 1 ? 's' : ''} pending — hover the ? for what data is needed
            </p>
          )}
          {sorted && (
            <ul className="space-y-2">
              {sorted.map((f) => {
                const vc = VERDICT_CONFIG[f.verdict as keyof typeof VERDICT_CONFIG] ?? VERDICT_CONFIG.not_checked;
                const meta = CHECK_CATALOG[f.checkId];
                const label = meta?.label ?? f.checkId;
                const isPending = f.verdict === 'not_checked';
                const prev = f.previousVerdict;
                const isFixed = f.verdict === 'pass' && (prev === 'fail' || prev === 'warning');
                const isRegressed = (f.verdict === 'fail' || f.verdict === 'warning') && prev === 'pass';
                return (
                  <li
                    key={f.checkId}
                    className={`flex items-center justify-between gap-3 py-2 border-b border-gray-50 last:border-0 ${isPending ? 'opacity-50' : ''}`}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <span className={`shrink-0 text-[9px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded ${
                        f.impactBand === 'high' ? 'bg-gray-800 text-white' :
                        f.impactBand === 'medium' ? 'bg-gray-200 text-gray-600' :
                        'bg-gray-100 text-gray-400'
                      }`}>{f.impactBand}</span>
                      <span className="text-xs text-gray-700 truncate">{label}</span>
                      {meta && <CheckTooltip meta={meta} isPending={isPending} />}
                      {isFixed && (
                        <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded bg-green-100 text-green-700 shrink-0">Fixed</span>
                      )}
                      {isRegressed && (
                        <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded bg-red-100 text-red-600 shrink-0">↓</span>
                      )}
                    </div>
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full shrink-0 ${vc.cls}`}>
                      {vc.label}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Main component ──────────────────────────────────────────────────────────

export function OverviewTab({ domain, onVerified: _onVerified, onAiTruthSetup }: Props) {
  const [reauditing, setReauditing]           = useState(false);
  const [auditError, setAuditError]           = useState('');
  const [latestAudit, setLatestAudit]         = useState<HistoryAuditEntry | null>(null);
  const [allAudits, setAllAudits]             = useState<HistoryAuditEntry[]>([]);
  const [auditInProgress, setAuditInProgress] = useState(false);
  const [citationRefreshKey, setCitationRefreshKey] = useState(0);
  const [findingsModal, setFindingsModal]     = useState<'seo' | 'aeo' | 'geo' | null>(null);
  const [schemaDeliveryOpen, setSchemaDeliveryOpen] = useState(false);
  const openFindings = useCallback((pillar: 'seo' | 'aeo' | 'geo') => setFindingsModal(pillar), []);
  const pollTimerRef  = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollStartRef  = useRef<number | null>(null);

  const { data: kwData } = useQuery({
    queryKey: ['kw-list', domain.id],
    queryFn:  () => api.keywords.list(domain.id),
    enabled:  domain.verified,
    staleTime: 5 * 60 * 1000,
  });

  const { data: blData } = useQuery({
    queryKey: ['bl-targets', domain.id],
    queryFn:  () => api.backlinkTargets.listByDomain(domain.id),
    enabled:  domain.verified,
    staleTime: 5 * 60 * 1000,
  });

  function stopPolling() {
    if (pollTimerRef.current !== null) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
    pollStartRef.current = null;
  }

  function startPolling() {
    if (pollTimerRef.current !== null) return;
    pollStartRef.current = Date.now();
    pollTimerRef.current = setInterval(async () => {
      if (pollStartRef.current !== null && Date.now() - pollStartRef.current > POLL_MAX_MS) {
        stopPolling();
        setAuditInProgress(false);
        return;
      }
      try {
        const res = await api.domains.getAudits(domain.id);
        if (res.audits.length > 0) {
          setLatestAudit(res.audits[res.audits.length - 1]!);
          setAllAudits(res.audits);
          stopPolling();
          setAuditInProgress(false);
        }
      } catch {
        // non-fatal — keep polling
      }
    }, POLL_INTERVAL_MS);
  }

  useEffect(() => {
    if (!domain.verified) return;
    api.domains.getAudits(domain.id)
      .then((res) => {
        if (res.audits.length > 0) {
          setLatestAudit(res.audits[res.audits.length - 1]!);
          setAllAudits(res.audits);
        }
      })
      .catch(() => { /* non-fatal */ });
    return () => { stopPolling(); };
  }, [domain.id, domain.verified]); // eslint-disable-line react-hooks/exhaustive-deps


  async function handleReaudit() {
    setReauditing(true);
    setAuditError('');
    try {
      await api.domains.reaudit(domain.id);
      setAuditInProgress(true);
      startPolling();
      setCitationRefreshKey((k) => k + 1);
    } catch (err) {
      setAuditError(err instanceof Error ? err.message : 'Failed to queue audit');
    } finally {
      setReauditing(false);
    }
  }

  const scores       = latestAudit && !auditInProgress
    ? (latestAudit.currentScores ?? latestAudit.originalScores)
    : null;
  const pagesCrawled = latestAudit?.pagesCrawled ?? null;
  const kwCount      = kwData?.keywords.length ?? null;
  const blCount      = blData?.targets.length ?? null;

  // A1 — derive lowConfidence from the stored confidence field.
  // 'high' = all key signals were measured; anything else = proxy data only.
  const auditConfidence = scores?.confidence ?? 'medium';
  const lowConfidence   = auditConfidence !== 'high';

  // A3 — new-domain floor: suppress pillar labels and overall score when the
  // domain has fewer than 3 completed audits or was created <30 days ago.
  const domainAgeDays = (Date.now() - new Date(domain.createdAt).getTime()) / (1000 * 60 * 60 * 24);
  const newDomainFloor = allAudits.length < NEW_DOMAIN_AUDIT_THRESHOLD || domainAgeDays < NEW_DOMAIN_DAY_THRESHOLD;

  const PILLARS = scores
    ? [
        { key: 'seo', label: 'SEO', score: scores.seo, desc: 'Search engine optimisation' },
        { key: 'aeo', label: 'AEO', score: scores.aeo, desc: 'AI answer engine optimisation' },
        { key: 'geo', label: 'GEO', score: scores.geo, desc: 'Generative engine optimisation' },
      ] as const
    : null;

  return (
    <div className="space-y-6">

      {/* ── Findings modal ───────────────────────────────────────────── */}
      {findingsModal && latestAudit && (
        <FindingsModal
          pillar={findingsModal}
          auditId={latestAudit.auditId}
          onClose={() => setFindingsModal(null)}
        />
      )}

      {/* ── Pillar score cards ──────────────────────────────────────── */}
      {scores && PILLARS && (
        <section>
          {/* A3 — new-domain accuracy note */}
          {newDomainFloor && (
            <div className="mb-3 rounded-lg bg-blue-50 border border-blue-100 px-4 py-2.5 text-xs text-blue-700">
              Score based on &lt;{NEW_DOMAIN_DAY_THRESHOLD} days of data — accuracy improves over time as more signals are collected.
            </div>
          )}
          <div className="grid grid-cols-3 gap-3">
            {PILLARS.map(({ key, label, score, desc }) => {
              // A1+A2: use canonical label; A3: suppress label when newDomainFloor
              const pillarLabel: ScoreLabel | null = newDomainFloor ? null : getScoreLabel(score, lowConfidence);
              const tone = pillarLabel ? getLabelTone(pillarLabel) : 'amber';
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => openFindings(key)}
                  className="text-left rounded-xl border border-gray-100 p-5 shadow-sm transition-shadow hover:shadow-md hover:border-gray-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-green"
                  style={{ background: TONE_PILLAR_BG[tone] }}
                  title={`View ${label} check details`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-[10px] font-semibold tracking-[0.14em] uppercase text-gray-500">
                      {label}
                    </span>
                    {pillarLabel && (
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${TONE_STATUS_CLS[tone]}`}>
                        {pillarLabel}
                      </span>
                    )}
                  </div>
                  <div className="flex items-baseline gap-1 mb-1">
                    <span className="text-3xl font-bold text-gray-900">{Math.round(score)}</span>
                    <span className="text-sm text-gray-400">/100</span>
                  </div>
                  <p className="text-xs text-gray-400">{desc}</p>
                  <p className="text-[10px] text-gray-300 mt-2">Tap to see checks →</p>
                </button>
              );
            })}
          </div>
          <p className="text-[11px] text-gray-400 mt-2 text-right">
            Last audited{' '}
            {new Date(latestAudit!.auditedAt).toLocaleDateString('en-GB', { dateStyle: 'medium' })}
            {pagesCrawled !== null && pagesCrawled > 1 ? ` · ${pagesCrawled} pages crawled` : ''}
          </p>
        </section>
      )}

      {/* ── Metric tiles ─────────────────────────────────────────────── */}
      {scores && (
        <section className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            {
              label: 'Pages audited',
              value: pagesCrawled !== null && pagesCrawled > 1
                ? pagesCrawled.toLocaleString()
                : '1',
              sub: pagesCrawled !== null && pagesCrawled > 1 ? 'Pro depth scan' : 'Site-level scan',
            },
            {
              label: 'Overall score',
              // A3: suppress overall when newDomainFloor — too early for a meaningful mean
              value: newDomainFloor ? '—' : Math.round((scores.seo + scores.aeo + scores.geo) / 3).toString(),
              sub: newDomainFloor ? 'Improves with time' : 'across all pillars',
            },
            {
              label: 'Keywords tracked',
              value: kwCount !== null ? kwCount.toLocaleString() : '—',
              sub: kwCount !== null ? 'tracked' : 'No data yet',
            },
            {
              label: 'Backlinks',
              value: blCount !== null ? blCount.toLocaleString() : '—',
              sub: blCount !== null ? 'opportunities' : 'No data yet',
            },
          ].map(({ label, value, sub }) => (
            <div key={label} className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm">
              <p className="text-[10px] font-semibold uppercase tracking-widest text-gray-400 mb-1">{label}</p>
              <p className="text-2xl font-bold text-gray-900">{value}</p>
              <p className="text-[11px] text-gray-400 mt-1">{sub}</p>
            </div>
          ))}
        </section>
      )}

      {/* ── Score history chart ──────────────────────────────────────── */}
      {allAudits.length >= 2 && (
        <section className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm">
          <h2 className="text-sm font-semibold text-gray-900 mb-4">Score history</h2>
          <ScoreHistoryChart audits={allAudits} />
        </section>
      )}

      {/* ── Pending verification ─────────────────────────────────────── */}
      {!domain.verified && (
        <section className="bg-amber-50 border border-amber-200 rounded-xl p-5">
          <h2 className="font-semibold text-amber-900 mb-1">Waiting for snippet</h2>
          <p className="text-sm text-amber-800">
            Your domain will be verified automatically the first time the snippet fires on your site.
            Make sure you&apos;ve added the snippet to your page{' '}
            <code className="bg-amber-100 px-1 rounded">&lt;head&gt;</code>.
          </p>
        </section>
      )}


      {/* ── Schema delivery (JSON-LD for AI crawlers) ───────────────── */}
      {domain.verified && (
        <div className="bg-white rounded-[6px] border border-slate-200">
          <button
            type="button"
            onClick={() => setSchemaDeliveryOpen((o) => !o)}
            className="w-full flex items-center justify-between px-6 py-4 text-left"
          >
            <span className="text-sm font-semibold text-[#0F172A]">Schema deployment</span>
            <span className="text-slate-400 text-xs">{schemaDeliveryOpen ? '▲ Hide' : '▼ Configure'}</span>
          </button>
          {schemaDeliveryOpen && (
            <div className="border-t border-slate-100 p-4 space-y-3">
              <DeliveryRouteChooser domainId={domain.id} initialMode={domain.schemaDeliveryMode ?? 'undecided'} />
              <SchemaDeliveryCard domainId={domain.id} />
            </div>
          )}
        </div>
      )}

      {/* ── Run / re-run audit ───────────────────────────────────────── */}
      {domain.verified && (
        <section className="bg-white border border-gray-100 rounded-xl p-6 shadow-sm">
          {auditInProgress ? (
            <div className="flex items-center gap-4">
              <div className="shrink-0 flex items-center justify-center w-10 h-10 rounded-full bg-brand-green/10">
                <span className="inline-block w-5 h-5 rounded-full border-2 border-brand-green border-t-transparent animate-spin" />
              </div>
              <div>
                <p className="font-semibold text-brand-ink text-sm">Audit in progress</p>
                <p className="text-sm text-gray-400 mt-0.5">
                  Analysing your site — results will appear here in ~2 minutes.
                </p>
              </div>
            </div>
          ) : (
            <div className="flex items-start justify-between">
              <div>
                <h2 className="font-semibold text-brand-ink mb-1">AI visibility audit</h2>
                <p className="text-sm text-gray-500">
                  Run a full audit to generate your schema, FAQ blocks, and suggestions.
                </p>
              </div>
              <button
                onClick={handleReaudit}
                disabled={reauditing}
                className="ml-4 shrink-0 bg-brand-green text-white text-sm font-medium px-4 py-2 rounded-lg hover:bg-brand-green-dark transition-colors disabled:opacity-50"
              >
                {reauditing ? 'Queuing…' : 'Run audit'}
              </button>
            </div>
          )}
          {auditError && (
            <p className="text-sm mt-3 px-3 py-2 rounded-lg bg-red-50 text-red-600">{auditError}</p>
          )}
        </section>
      )}

      {/* ── AI Citation Visibility ────────────────────────────────────── */}
      {domain.verified && (
        <section className="bg-white border border-gray-100 rounded-xl p-6 shadow-sm">
          <h2 className="font-semibold text-brand-ink mb-4">AI Citation Visibility</h2>
          <CitationHistorySection
            domainId={domain.id}
            onTriggerAudit={handleReaudit}
            refreshKey={citationRefreshKey}
          />
        </section>
      )}

      {/* ── AI Truth ─────────────────────────────────────────────────── */}
      {onAiTruthSetup && (
        <section className="bg-white border border-gray-100 rounded-xl p-6 shadow-sm">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="font-semibold text-brand-ink mb-1">AI Truth</h2>
              <p className="text-sm text-gray-500">
                Make your verified answers directly retrievable by AI platforms — no SEO required.
              </p>
            </div>
            <button
              onClick={onAiTruthSetup}
              data-testid="btn-ai-truth-setup"
              className="shrink-0 ml-4 bg-brand-green text-white text-sm font-medium px-4 py-2 rounded-lg hover:bg-brand-green-dark transition-colors"
            >
              Set up →
            </button>
          </div>
        </section>
      )}

    </div>
  );
}
